import { Link, NavLink } from "react-router-dom";
import {
  DEALER_SORT_OPTIONS,
  DEALER_STATUSES,
  PLAN_TYPES,
  TRIAL_STATUSES,
  VERIFICATION_STATUSES,
  formatDate,
  formatDealerStatus,
} from "../../../constants/automapDealerOnboardingConstants";

export function AdminDealerNav({ active }) {
  const items = [
    { key: "list", label: "Concesionarios", href: "/admin/dealers" },
    { key: "founding", label: "Fundadores", href: "/admin/dealers/founding" },
    { key: "prospects", label: "Prospectos", href: "/admin/dealers/prospects" },
    { key: "new", label: "Nuevo", href: "/admin/dealers/new" },
    { key: "verifications", label: "Verificaciones", href: "/admin/dealers/verifications" },
    { key: "trials", label: "Trials", href: "/admin/dealers/trials" },
    { key: "plans", label: "Planes", href: "/admin/plans" },
  ];
  return (
    <nav className="admin-console__nav-tabs">
      {items.map((item) => (
        <Link
          key={item.key}
          to={item.href}
          className={active === item.key ? "is-active" : undefined}
        >
          {item.label}
        </Link>
      ))}
    </nav>
  );
}

export function DealerDetailNav({ dealerId, active }) {
  const base = `/admin/dealers/${dealerId}`;
  const tabs = [
    { key: "overview", label: "Resumen", href: base },
    { key: "contacts", label: "Contactos", href: `${base}/contacts` },
    { key: "lead-preferences", label: "Leads", href: `${base}/lead-preferences` },
    { key: "locations", label: "Ubicaciones", href: `${base}/locations` },
    { key: "users", label: "Usuarios", href: `${base}/users` },
    { key: "verification", label: "Verificación", href: `${base}/verification` },
    { key: "onboarding", label: "Onboarding", href: `${base}/onboarding` },
    { key: "trial", label: "Trial", href: `${base}/trial` },
    { key: "plan", label: "Plan", href: `${base}/plan` },
    { key: "performance", label: "Rendimiento", href: `${base}/performance` },
    { key: "notes", label: "Notas", href: `${base}/notes` },
    { key: "activity", label: "Actividad", href: `${base}/activity` },
  ];
  return (
    <nav className="admin-console__nav-tabs">
      {tabs.map((tab) => (
        <NavLink
          key={tab.key}
          to={tab.href}
          end={tab.key === "overview"}
          className={({ isActive }) => (isActive || active === tab.key ? "is-active" : undefined)}
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function statusBadgeClass(status) {
  if (["active", "verified", "converted"].includes(status)) return "admin-badge--success";
  if (["pending", "onboarding", "active", "ending_soon"].includes(status)) return "admin-badge--info";
  if (["paused", "not_started", "prospect"].includes(status)) return "admin-badge--muted";
  if (["rejected", "suspended", "expired", "declined"].includes(status)) return "admin-badge--danger";
  return "admin-badge--warning";
}

export function formatDealerRow(dealer) {
  return {
    name: dealer.businessName,
    city: dealer.city || "—",
    status: formatDealerStatus(dealer.status),
    verification: formatDealerStatus(dealer.verificationStatus),
    trial: formatDealerStatus(dealer.trialStatus),
    plan: dealer.planType || "—",
    publicProfile: dealer.publicProfileEnabled ? "Sí" : "No",
    listings: dealer.activeListingCount ?? dealer.metrics?.activeListings ?? 0,
    leads: dealer.leadsReceived ?? dealer.metrics?.leadsReceived ?? 0,
    whatsapp: dealer.mainWhatsapp || "—",
    owner: dealer.assignedSalesOwner || dealer.assignedSalesOwnerId || "—",
    lastActivity: formatDate(dealer.lastActivity || dealer.lastActivityAt),
    created: formatDate(dealer.createdAt),
  };
}

export function DealerFilters({
  search,
  city,
  status,
  verificationStatus,
  trialStatus,
  planType,
  assignedSalesOwner,
  publicProfileEnabled,
  hasActiveListings,
  hasStaleInventory,
  trialEndingSoon,
  sort,
  onSearch,
  onCity,
  onStatus,
  onVerificationStatus,
  onTrialStatus,
  onPlanType,
  onAssignedSalesOwner,
  onPublicProfileEnabled,
  onHasActiveListings,
  onHasStaleInventory,
  onTrialEndingSoon,
  onSort,
}) {
  return (
    <div className="admin-filters" style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1rem" }}>
      <input type="search" placeholder="Buscar concesionario" value={search} onChange={(e) => onSearch(e.target.value)} />
      <input type="text" placeholder="Ciudad" value={city} onChange={(e) => onCity(e.target.value)} />
      <input type="text" placeholder="Sales owner" value={assignedSalesOwner} onChange={(e) => onAssignedSalesOwner(e.target.value)} />
      <select value={status} onChange={(e) => onStatus(e.target.value)}>
        <option value="">Estado</option>
        {DEALER_STATUSES.map((value) => (
          <option key={value} value={value}>{formatDealerStatus(value)}</option>
        ))}
      </select>
      <select value={verificationStatus} onChange={(e) => onVerificationStatus(e.target.value)}>
        <option value="">Verificación</option>
        {VERIFICATION_STATUSES.map((value) => (
          <option key={value} value={value}>{formatDealerStatus(value)}</option>
        ))}
      </select>
      <select value={trialStatus} onChange={(e) => onTrialStatus(e.target.value)}>
        <option value="">Trial</option>
        {TRIAL_STATUSES.map((value) => (
          <option key={value} value={value}>{formatDealerStatus(value)}</option>
        ))}
      </select>
      <select value={planType} onChange={(e) => onPlanType(e.target.value)}>
        <option value="">Plan</option>
        {PLAN_TYPES.map((value) => (
          <option key={value} value={value}>{value}</option>
        ))}
      </select>
      <select value={publicProfileEnabled} onChange={(e) => onPublicProfileEnabled(e.target.value)}>
        <option value="">Perfil público</option>
        <option value="true">Habilitado</option>
        <option value="false">Deshabilitado</option>
      </select>
      <select value={hasActiveListings} onChange={(e) => onHasActiveListings(e.target.value)}>
        <option value="">Inventario activo</option>
        <option value="true">Con listings</option>
        <option value="false">Sin listings</option>
      </select>
      <select value={hasStaleInventory} onChange={(e) => onHasStaleInventory(e.target.value)}>
        <option value="">Inventario obsoleto</option>
        <option value="true">Con obsoletos</option>
      </select>
      <select value={trialEndingSoon} onChange={(e) => onTrialEndingSoon(e.target.value)}>
        <option value="">Trial por vencer</option>
        <option value="true">≤14 días</option>
      </select>
      <select value={sort} onChange={(e) => onSort(e.target.value)}>
        {DEALER_SORT_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );
}

export function ConfirmDialog({ open, title, message, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="admin-dialog-backdrop" role="dialog" aria-modal="true">
      <div className="admin-dialog">
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="admin-console__header-actions">
          <button type="button" className="admin-btn" onClick={onCancel}>Cancelar</button>
          <button type="button" className="admin-btn admin-btn--primary" onClick={onConfirm}>Confirmar</button>
        </div>
      </div>
    </div>
  );
}
